/**
 * Seguridad · Roles y permisos — matriz read-only.
 *
 * Fuente de verdad: `CATALOGO_ACCIONES` + `permisoDeRol` en `domain/rbac`.
 * Esta vista NO duplica reglas — solo las renderiza. Si se agrega una acción
 * al catálogo, aparece automáticamente acá.
 *
 * GAMP 5: revisión periódica de roles y permisos por el dueño del sistema.
 * En producción esta tabla se exporta como evidencia para auditorías.
 */

import { useMemo, useState } from 'react';
import { Check, X, AlertCircle, Shield, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { CATALOGO_ACCIONES, permisoDeRol } from '@/domain/rbac';
import type { Rol } from '@/domain/types';

const ROLES: readonly Rol[] = ['Administrador', 'Gerente de Logística', 'Operador'] as const;

const GRUPO_BADGE = {
  Operativo: 'badge-blue',
  Lectura: 'badge-gray',
  Crítica: 'badge-red',
  Administración: 'badge-purple',
} as const;

const GRUPO_DESCRIPCION = {
  Operativo: 'Acciones que mutan estado y producen una operación en el SNT.',
  Lectura: 'Consultas sin efectos colaterales.',
  Crítica: 'Mutaciones reversivas o destructivas — requieren firma o razón.',
  Administración: 'Operaciones sobre configuración y control del sistema.',
} as const;

export function RolesPage() {
  const [filtroGrupo, setFiltroGrupo] = useState<string>('all');

  const acciones = useMemo(() => {
    if (filtroGrupo === 'all') return CATALOGO_ACCIONES;
    return CATALOGO_ACCIONES.filter((a) => a.grupo === filtroGrupo);
  }, [filtroGrupo]);

  // Resumen por rol — total permitido y % cobertura
  const resumen = useMemo(() => {
    return ROLES.map((rol) => {
      const permitidos = CATALOGO_ACCIONES.filter(
        (a) => permisoDeRol(rol, a.accion) === true,
      ).length;
      const condicionales = CATALOGO_ACCIONES.filter(
        (a) => permisoDeRol(rol, a.accion) === 'depende',
      ).length;
      return {
        rol,
        permitidos,
        condicionales,
        total: CATALOGO_ACCIONES.length,
      };
    });
  }, []);

  return (
    <div className="space-y-4">
      {/* Resumen por rol */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {resumen.map((r) => (
          <Card key={r.rol}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                {r.rol === 'Administrador' ? (
                  <ShieldCheck className="h-8 w-8 text-purple-600" />
                ) : r.rol === 'Gerente de Logística' ? (
                  <ShieldCheck className="h-8 w-8 text-amber-600" />
                ) : (
                  <Shield className="h-8 w-8 text-blue-600" />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'badge',
                        r.rol === 'Administrador'
                          ? 'badge-purple'
                          : r.rol === 'Gerente de Logística'
                            ? 'badge-orange'
                            : 'badge-blue',
                      )}
                    >
                      {r.rol}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    <b>{r.permitidos}</b> de {r.total} acciones permitidas
                    {r.condicionales > 0 && (
                      <>
                        {' '}
                        · <span className="text-amber-700">{r.condicionales} condicional</span>
                      </>
                    )}
                  </div>
                  <div className="mt-1 text-[11px] text-gray-600">
                    {r.rol === 'Administrador'
                      ? 'Acceso total al sistema (incluye configuración).'
                      : r.rol === 'Gerente de Logística'
                        ? 'Operativo + visibilidad de audit/sesiones + configuración de eventos ANMAT.'
                        : 'Acceso operativo + consultas. Sin administración.'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 pt-4">
          <Alert className="border-blue-200 bg-blue-50">
            <AlertCircle className="h-4 w-4 text-blue-700" />
            <AlertDescription className="text-xs text-blue-900">
              Matriz <b>read-only</b> derivada del catálogo en{' '}
              <code className="font-mono">src/domain/rbac</code>. Cambios en los permisos
              requieren modificar código + audit del cambio + versión nueva del sistema
              validada (GAMP 5).
            </AlertDescription>
          </Alert>

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Filtrar por grupo:</label>
            <Select value={filtroGrupo} onValueChange={setFiltroGrupo}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Operativo">Operativo</SelectItem>
                <SelectItem value="Lectura">Lectura</SelectItem>
                <SelectItem value="Crítica">Crítica</SelectItem>
                <SelectItem value="Administración">Administración</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-[11px] text-gray-600">
              {acciones.length} acción{acciones.length !== 1 ? 'es' : ''}
            </span>
          </div>

          <div className="overflow-x-auto rounded border border-gray-100">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/3">Acción</TableHead>
                  <TableHead>Grupo</TableHead>
                  {ROLES.map((rol) => (
                    <TableHead key={rol} className="text-center">
                      {rol}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {acciones.map((a) => (
                  <TableRow key={a.accion}>
                    <TableCell>
                      <div className="text-xs font-semibold text-gray-800">{a.label}</div>
                      <div className="text-[11px] text-gray-600">{a.descripcion}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-gray-600">
                        {a.accion}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn('badge', GRUPO_BADGE[a.grupo])}
                        title={GRUPO_DESCRIPCION[a.grupo]}
                      >
                        {a.grupo}
                      </span>
                    </TableCell>
                    {ROLES.map((rol) => {
                      const permiso = permisoDeRol(rol, a.accion);
                      return (
                        <TableCell key={rol} className="text-center">
                          {permiso === true && (
                            <Check className="mx-auto h-4 w-4 text-green-600" />
                          )}
                          {permiso === false && (
                            <X className="mx-auto h-4 w-4 text-gray-300" />
                          )}
                          {permiso === 'depende' && (
                            <span
                              className="badge badge-yellow"
                              title="Permitido bajo condición — ver Multi-GLN en RBAC"
                            >
                              Si 2+ GLN
                            </span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="text-[11px] text-gray-600">
            <b>Convención:</b>{' '}
            <Check className="inline h-3 w-3 text-green-600" /> permitido siempre ·{' '}
            <X className="inline h-3 w-3 text-gray-300" /> nunca permitido ·{' '}
            <span className="badge badge-yellow">Condicional</span> requiere condición
            adicional (ej. switch_location requiere 2+ GLNs operables).
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
